import { useState } from "react";
import { Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { formatApiError } from "@/auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const QuestionnaireForm = ({ t, onDone }) => {
  const [form, setForm] = useState({
    pain_type: "",
    goal: "",
    equipment: "",
    activity_level: "moderate",
    extra_notes: "",
  });
  const [loading, setLoading] = useState(false);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/my/questionnaire`, form, { withCredentials: true });
      toast.success(t("questionnaire.success"));
      onDone(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full px-4 py-3 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-[#2c4c3b]/20 text-sm bg-white";
  const labelClass = "block text-sm font-medium text-stone-700 mb-2";

  return (
    <div className="bg-white rounded-3xl p-6 sm:p-8 border border-stone-100 shadow-[0_8px_30px_rgba(0,0,0,0.06)]" data-testid="questionnaire-form">
      <h2 className="font-serif text-2xl text-stone-900 mb-2">{t("questionnaire.title")}</h2>
      <p className="text-sm text-stone-600 mb-6">{t("questionnaire.subtitle")}</p>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className={labelClass}>{t("questionnaire.painType")} *</label>
          <textarea
            value={form.pain_type}
            onChange={set("pain_type")}
            required
            rows={2}
            placeholder={t("questionnaire.painPlaceholder")}
            className={`${inputClass} resize-none`}
            data-testid="questionnaire-pain-input"
          />
        </div>
        <div>
          <label className={labelClass}>{t("questionnaire.goal")} *</label>
          <textarea
            value={form.goal}
            onChange={set("goal")}
            required
            rows={2}
            placeholder={t("questionnaire.goalPlaceholder")}
            className={`${inputClass} resize-none`}
            data-testid="questionnaire-goal-input"
          />
        </div>
        <div>
          <label className={labelClass}>{t("questionnaire.equipment")} *</label>
          <input
            type="text"
            value={form.equipment}
            onChange={set("equipment")}
            required
            placeholder={t("questionnaire.equipmentPlaceholder")}
            className={inputClass}
            data-testid="questionnaire-equipment-input"
          />
        </div>
        <div>
          <label className={labelClass}>{t("questionnaire.activityLevel")} *</label>
          <select
            value={form.activity_level}
            onChange={set("activity_level")}
            className={inputClass}
            data-testid="questionnaire-activity-select"
          >
            {["sedentary", "light", "moderate", "active"].map((level) => (
              <option key={level} value={level}>
                {t(`questionnaire.levels.${level}`)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelClass}>{t("questionnaire.notes")}</label>
          <textarea
            value={form.extra_notes}
            onChange={set("extra_notes")}
            rows={2}
            className={`${inputClass} resize-none`}
            data-testid="questionnaire-notes-input"
          />
        </div>
        <Button
          type="submit"
          disabled={loading}
          className="w-full bg-[#2c4c3b] hover:bg-[#1a2e22] text-stone-50 rounded-full py-6"
          data-testid="questionnaire-submit-btn"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
            <>
              <Send className="w-4 h-4 mr-2" />
              {t("questionnaire.submit")}
            </>
          )}
        </Button>
      </form>
    </div>
  );
};

export default QuestionnaireForm;
